import { useEffect, useState } from 'react'
import { fdeProjectPath, fdeRoutes } from '@/frontend/features/Fde/routes'
import type { Id, SemanticObjectViewResult } from '@/frontend/features/Fde/types'

interface Options {
  projectId: Id
  classId: Id
  objectId: string | null
  errorLabel: string
}

export default function useSemanticObjectView(options: Options) {
  const [result, setResult] = useState<SemanticObjectViewResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!options.objectId) {
      setResult(null)
      setError(null)
      return
    }

    const controller = new AbortController()
    setLoading(true)
    setError(null)
    fetch(fdeProjectPath(fdeRoutes.semanticObjects, String(options.projectId)), {
      method: 'POST',
      credentials: 'same-origin',
      signal: controller.signal,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '',
      },
      body: JSON.stringify({ class_id: options.classId, object_id: options.objectId, limit: 12 }),
    })
      .then(async (response) => {
        const payload = await response.json()
        if (!response.ok) throw new Error(payload.error || options.errorLabel)
        return payload as SemanticObjectViewResult
      })
      .then(setResult)
      .catch((requestError: Error) => {
        if (requestError.name !== 'AbortError') setError(requestError.message)
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false) })

    return () => controller.abort()
  }, [options.classId, options.errorLabel, options.objectId, options.projectId])

  return { result, error, loading }
}
